import React, { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import type { MarketplaceCollectionRef, MarketplaceListing } from '@mprolab/sdk'
import { useUnclaimedForToken } from 'utils/marketplace/useUnclaimedForToken'
import TypeBadge from './TypeBadge'
import ExpiryNote from './ExpiryNote'

const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    transform 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan};
    transform: translateY(-1px);
  }
`

const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  color: ${({ theme }) => theme.colors.cyan};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const PriceRow = styled(Flex)`
  align-items: baseline;
  justify-content: space-between;
  margin-top: 4px;
`

interface ListingCardProps {
  listing: MarketplaceListing
  collections: MarketplaceCollectionRef[]
  /** A person's public name where they set one; the short address otherwise. */
  displayName?: (wallet: string) => string
  onClick: () => void
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, collections, onClick, displayName }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()

  const label = useMemo(
    () =>
      collections.find((c) => c.nft === listing.nft)?.label ?? `${listing.nft.slice(0, 6)}…${listing.nft.slice(-4)}`,
    [collections, listing.nft]
  )
  const priceMpro = useMemo(() => {
    try {
      return ethers.utils.formatEther(listing.priceWei)
    } catch {
      return '?'
    }
  }, [listing.priceWei])
  const sellerShort = displayName?.(listing.seller) ?? `${listing.seller.slice(0, 6)}…${listing.seller.slice(-4)}`
  // What the buyer also receives — stated by the platform, not by the seller.
  const unclaimed = useUnclaimedForToken(listing.nft, String(listing.tokenId))

  return (
    <Card type="button" onClick={onClick}>
      <Flex justifyContent="space-between" alignItems="center">
        <TypeBadge />
        <Tag>{label}</Tag>
      </Flex>
      <Text variant={TextVariant.BODY_DEFAULT_BOLD} isTrimmed linesToDisplay={1}>
        {label} #{listing.tokenId}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.seller')}: {sellerShort}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.listedOn', {
          date: dayjs.unix(listing.createdAt).format('YYYY-MM-DD')
        })}
      </Text>
      {unclaimed.unclaimedMpro !== null && unclaimed.unclaimedMpro > 0 && (
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.green}>
          {t('marketplace.carriesUnclaimed', {
            amount: unclaimed.unclaimedMpro.toLocaleString('en-US', {
              maximumFractionDigits: 2
            })
          })}
        </Text>
      )}
      <ExpiryNote expiresAt={listing.expiresAt} />
      <PriceRow>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {t('marketplace.price')}
        </Text>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan}>
          {priceMpro} MPRO
        </Text>
      </PriceRow>
    </Card>
  )
}

export default ListingCard
